import type { MetaAd } from '@storefronts/common-types'
import { upscaleCloudinaryPosterUrl } from '@storefronts/common-types'

const DISPLAY_IMAGE_WIDTH = 900

function isLikelyVideoUrl(url: string): boolean {
    if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) {
        return true
    }

    try {
        const { hostname, pathname } = new URL(url)
        const host = hostname.toLowerCase().replace(/^www\./, '')

        if (host === 'instagram.com') {
            return /^\/(p|reel|reels|tv)\//.test(pathname)
        }

        if (host === 'facebook.com' || host === 'fb.watch') {
            return (
                pathname.includes('/videos/') ||
                pathname.includes('/video.php') ||
                pathname.includes('/watch') ||
                pathname.includes('/reel/')
            )
        }

        return false
    } catch {
        return false
    }
}

function isLikelyImageUrl(url: string): boolean {
    if (isLikelyVideoUrl(url)) {
        return false
    }

    return (
        /\.(jpg|jpeg|png|gif|webp|avif)(\?|$)/i.test(url) ||
        url.includes('/image/upload/') ||
        url.includes('cloudfront.net')
    )
}

export function upscaleImageUrl(
    url: string,
    targetWidth = DISPLAY_IMAGE_WIDTH,
): string {
    const cloudinaryPosterUrl = upscaleCloudinaryPosterUrl(url)
    if (cloudinaryPosterUrl !== url) {
        return cloudinaryPosterUrl
    }

    try {
        const parsed = new URL(url)
        const widthParam = parsed.searchParams.get('w')

        if (widthParam) {
            const currentWidth = Number(widthParam)
            if (!Number.isNaN(currentWidth) && currentWidth >= targetWidth) {
                return url
            }

            parsed.searchParams.set('w', String(targetWidth))
            return parsed.toString()
        }

        if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
            if (/\/upload\/w_\d+/.test(url)) {
                return url.replace(
                    /\/upload\/w_\d+/,
                    `/upload/w_${targetWidth}`,
                )
            }

            return url.replace(
                '/upload/',
                `/upload/w_${targetWidth},c_limit,q_auto:good/`,
            )
        }
    } catch {
        return url
    }

    return url
}

export function getMetaAdBestImageUrl(ad: MetaAd): string | undefined {
    const candidates = [ad.creativeImageUrl, ad.mediaUrl, ad.thumbnailUrl]

    for (const url of candidates) {
        if (url && isLikelyImageUrl(url)) {
            return upscaleImageUrl(url)
        }
    }

    return undefined
}

export function getMetaAdPreviewImageUrl(ad: MetaAd): string | undefined {
    return getMetaAdBestImageUrl(ad)
}

export function getMetaAdHoverImageUrl(ad: MetaAd): string | undefined {
    return getMetaAdBestImageUrl(ad)
}

export function getMetaAdVideoUrl(ad: MetaAd): string | undefined {
    if (ad.type !== 'VIDEO') {
        return undefined
    }

    if (ad.videoUrl) {
        return ad.videoUrl
    }

    if (!ad.mediaUrl || isLikelyImageUrl(ad.mediaUrl)) {
        return undefined
    }

    return ad.mediaUrl
}
